import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';

const LoginModal = ({ isOpen, onClose }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { setIsAuthenticated } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/login.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
                credentials: 'include' // Important for session cookies
            });

            if (response.ok) {
                setIsAuthenticated(true);
                onClose();
                navigate('/admin');
            } else {
                const text = await response.text();
                try {
                    const data = JSON.parse(text);
                    setError(data.error || 'Invalid credentials');
                } catch (e) {
                    console.error('Server response not JSON:', text);
                    setError('Server error. Check console for details.');
                }
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('Network error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-black/90 backdrop-blur-xl"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                        className="bg-[#111] border border-white/10 p-8 md:p-12 max-w-md w-full relative"
                        onClick={e => e.stopPropagation()}
                    >
                        <button onClick={onClose} className="absolute top-6 right-6 text-zinc-500 hover:text-[#00f3ff]"><X size={24} /></button>
                        <h3 className="text-3xl font-black uppercase tracking-tighter mb-8 text-[#00f3ff]">Admin Access</h3>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <input
                                type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                                placeholder="Username" required
                                className="w-full bg-zinc-900/50 border-b border-white/10 p-4 focus:border-[#00f3ff] outline-none transition-all"
                            />
                            <input
                                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password" required
                                className="w-full bg-zinc-900/50 border-b border-white/10 p-4 focus:border-[#00f3ff] outline-none transition-all"
                            />
                            <button type="submit" disabled={isSubmitting} className="w-full bg-white text-black py-4 font-black uppercase hover:bg-[#00f3ff] transition-all tracking-[0.2em] disabled:opacity-50">
                                {isSubmitting ? 'Authenticating...' : 'Login'}
                            </button>
                            {error && <p className="text-center text-red-500 text-xs uppercase tracking-widest">{error}</p>}
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default LoginModal;